"use client";

import React, { forwardRef, useState, useEffect, useImperativeHandle, useRef } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "../VoucherTemplate/Invoice.css";

const InvoiceGenerator = forwardRef(({ salesData }, ref) => {
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [saved, setSaved] = useState(false);
  const [templateConfig, setTemplateConfig] = useState(null);
  const invoiceRef = useRef();

  useEffect(() => {
    if (salesData?.invoice_id || salesData?.id) {
      setInvoice({
        id: salesData.invoice_id || salesData.id,
        invoice_number: salesData.invoice_number || salesData.invoice_id || salesData.id,
        customer: salesData.customer_name,
        email: salesData.customer_email,
        date: salesData.sales_date?.split("T")[0] || new Date().toISOString().split("T")[0],
        items: salesData.items || [],
        total: Number(salesData.total_amount) || 0,
        tax: Number(salesData.tax) || 0,
        discount: Number(salesData.discount) || 0,
        grandTotal: Number(salesData.grand_total) || Number(salesData.total_amount) || 0,
      });
      setSaved(!!salesData.invoice_id);
    }
  }, [salesData]);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await axios.get("/api/templates/active/invoice");
        if (res.data) {
          setTemplateConfig(res.data.config || {});
        }
      } catch (err) {
        console.error("Failed to load active invoice template:", err);
      }
    };
    fetchTemplate();
  }, []);

  const handleSaveInvoice = async () => {
    if (!salesData?.id) return alert("Save the sale first to generate invoice.");
    setLoading(true);
    try {
      // Use standard axios instance
      const res = await axios.post(`/api/invoices/generate/${salesData.id}`);
      const savedInvoice = res.data;
      setInvoice({
        id: savedInvoice.id,
        invoice_number: savedInvoice.invoice_number,
        customer: savedInvoice.customer_name,
        email: savedInvoice.customer_email,
        date: savedInvoice.issue_date,
        items: salesData.items || [],
        total: Number(savedInvoice.total_amount) || 0,
        tax: Number(savedInvoice.tax) || 0,
        discount: Number(savedInvoice.discount) || 0,
        grandTotal: Number(savedInvoice.grand_total) || 0,
      });
      setSaved(true);
      alert(`Invoice #${savedInvoice.invoice_number} saved successfully!`);
    } catch (err) {
      console.error(err);
      alert("Failed to save invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = () => alert("Email feature coming soon!");

  const handleDownload = () => {
    if (!invoice || !saved) return alert("Save invoice first");
    const input = invoiceRef.current;
    
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Invoice_${invoice.invoice_number}.pdf`);
    });
  };

  useImperativeHandle(ref, () => ({ handleSaveInvoice, handleEmail, handleDownload }));

  if (!salesData) return <p className="p-4 text-center text-muted">No sales data available. Save the sale first.</p>;

  const config = templateConfig || {};
  const areaStyles = config.areaStyles || [];
  
  const getStyle = (areaName) => {
    const area = areaStyles.find(a => a.area === areaName);
    if (!area) return {};
    return {
      backgroundColor: area.bgColor,
      color: area.fontColor,
      fontSize: `${area.fontSize}px`,
      fontFamily: config.defaultFont || "Arial",
    };
  };

  const globalStyle = {
    fontFamily: config.defaultFont || "Arial",
    color: config.defaultFontColor || "#000",
    fontSize: `${config.defaultFontSize || 12}px`,
    lineHeight: "1.5",
    backgroundColor: "#fff",
  };

  return (
    <div className="invoice-preview mt-6">
      <div className="flex gap-3 justify-end mb-6 no-print">
        {!saved && (
          <Button onClick={handleSaveInvoice} className="bg-green-600 hover:bg-green-700 text-white shadow-md">
            {loading ? "Saving..." : "💾 Save Invoice"}
          </Button>
        )}
        <Button onClick={handleEmail} variant="outline" disabled={!saved} className="border-[#0077cc] text-[#0077cc]">📧 Email</Button>
        <Button onClick={handleDownload} className="bg-[#0077cc] hover:bg-[#005fa3] text-white shadow-md" disabled={!saved}>📄 Download PDF</Button>
      </div>

      <div className="invoice-wrapper" style={{background: "#f8fafc", padding: "40px", display: "flex", justifyContent: "center"}}>
        <div 
          className="invoice-container shadow-2xl" 
          ref={invoiceRef} 
          style={{
            ...globalStyle, 
            width: "210mm", 
            minHeight: "297mm", 
            padding: "20mm",
            boxSizing: "border-box",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            borderTop: "10px solid #002040",
            background: "#fff"
          }}
        >
          
          {/* HEADER SECTION */}
          <div className="flex justify-between items-start mb-8">
            <div className="flex flex-col gap-4">
              <div className="flex gap-4 items-center">
                {(config.logoUrl && config.logoUrl !== "null" && config.logoUrl !== "undefined") ? (
                  <img src={config.logoUrl} alt="Logo" style={{maxHeight: 80, width: "auto"}} crossOrigin="anonymous"/>
                ) : (
                  <img src="/rekonic-logo.jpg" alt="Rekonic Logo" style={{maxHeight: 70, width: "auto"}} crossOrigin="anonymous"/>
                )}
                <div className="h-12 w-[1px] bg-gray-200"></div>
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/IATA_logo.svg/320px-IATA_logo.svg.png" alt="IATA" style={{maxHeight: 40}} />
              </div>
              <div className="text-[11px] text-gray-600 font-medium">
                <p>REKONIC TRAVEL LLC</p>
                <p>SHARJAH, UNITED ARAB EMIRATES</p>
                <p>Email: info@rekonic.com | Web: www.rekonic.com</p>
              </div>
            </div>

            <div className="text-right flex flex-col items-end">
              <div className="qr-code mb-4 p-1 bg-white border border-gray-100 shadow-sm" style={{width: 90, height: 90}}>
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=90x90&data=${invoice?.invoice_number || "DRAFT"}`} alt="QR" />
              </div>
              <div className="space-y-1">
                <div className="bg-[#002040] text-white px-3 py-1 text-[12px] font-bold tracking-wider rounded-sm">
                  INVOICE #{invoice?.invoice_number || "DRAFT"}
                </div>
                <div className="text-[10px] text-gray-500 mt-2 font-semibold uppercase tracking-tight">
                  <p>TRN: 100519322000003</p>
                  <p>Issue Date: {invoice?.date}</p>
                  <p>Staff: {salesData?.created_by || "Admin"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* TITLE BAR */}
          <div className="text-center mb-8 relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-100"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-6 text-xl font-black text-[#002040] tracking-[0.2em] uppercase">
                TAX INVOICE - الفاتورة الضريبية
              </span>
            </div>
          </div>

          {/* BILLING & SUMMARY */}
          <div className="grid grid-cols-2 gap-8 mb-10">
            <div className="p-5 border border-gray-100 rounded-lg bg-gray-50/50">
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Billed To / فاتورة إلى</h3>
              <div className="text-blue-900 font-black text-lg mb-1">{invoice?.customer || "Valued Customer"}</div>
              <div className="text-[12px] text-gray-600 space-y-1">
                <p className="flex items-center gap-2 font-medium">
                  <span className="w-2 h-2 bg-[#00aaff] rounded-full"></span>
                  ACC ID: {salesData?.customer_id || "N/A"}
                </p>
                <p className="opacity-80">SHARJAH, UAE</p>
                <p className="opacity-80">{salesData?.customer_phone || ""}</p>
              </div>
            </div>

            <div className="flex flex-col justify-between">
              <div className="grid grid-cols-2 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-gray-50 p-3 text-center">
                  <p className="text-[9px] text-gray-400 font-bold uppercase">Invoice Date</p>
                  <p className="text-[11px] font-bold text-gray-700">{invoice?.date}</p>
                </div>
                <div className="bg-[#002040] p-3 text-center text-white">
                  <p className="text-[9px] opacity-70 font-bold uppercase">Amount Due</p>
                  <p className="text-sm font-black">AED {Number(invoice?.grandTotal).toFixed(2)}</p>
                </div>
              </div>
              <div className="mt-4 p-4 border border-blue-100 bg-blue-50/30 rounded-lg text-[10px] text-blue-900 italic leading-snug">
                "We appreciate your business. Please ensure payment is made by the due date to avoid service interruption."
              </div>
            </div>
          </div>

          {/* SERVICES TABLE */}
          <div className="flex-grow">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#002040] text-white text-[10px] font-bold tracking-wider">
                  <th className="p-4 text-left rounded-tl-lg border-b-2 border-blue-400">DESCRIPTION / الوصف</th>
                  <th className="p-4 text-center border-b-2 border-blue-400">PRICE / السعر</th>
                  <th className="p-4 text-center border-b-2 border-blue-400">QTY / الكمية</th>
                  <th className="p-4 text-center border-b-2 border-blue-400">VAT / الضريبة</th>
                  <th className="p-4 text-right rounded-tr-lg border-b-2 border-blue-400">TOTAL / الإجمالي</th>
                </tr>
              </thead>
              <tbody className="text-[11px] text-gray-700">
                {invoice?.items?.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <td className="p-4 align-top">
                      <div className="font-bold text-[#002040] text-[12px]">{item.item_name}</div>
                      <div className="text-gray-500 mt-1 italic whitespace-pre-wrap leading-relaxed">{item.description}</div>
                    </td>
                    <td className="p-4 text-center font-medium">AED {Number(item.selling_price).toFixed(2)}</td>
                    <td className="p-4 text-center font-medium">{item.quantity}</td>
                    <td className="p-4 text-center">
                      <div className="font-bold">AED {Number(salesData.tax || 0).toFixed(2)}</div>
                      <div className="text-[8px] text-gray-400">(5.0%)</div>
                    </td>
                    <td className="p-4 text-right font-black text-[#002040] text-[13px]">AED {Number(item.total_amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TOTALS SECTION */}
          <div className="mt-6 flex justify-end">
            <div className="w-[300px] space-y-2 p-6 bg-gray-50 rounded-xl border border-gray-100 shadow-inner">
              <div className="flex justify-between text-[11px] text-gray-500 font-bold uppercase">
                <span>Sub-Total</span>
                <span>AED {Number(invoice?.total).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[11px] text-gray-500 font-bold uppercase">
                <span>VAT (5%)</span>
                <span>AED {Number(invoice?.tax).toFixed(2)}</span>
              </div>
              {Number(invoice?.discount) > 0 && (
                <div className="flex justify-between text-[11px] text-red-500 font-bold uppercase">
                  <span>Discount</span>
                  <span>-AED {Number(invoice?.discount).toFixed(2)}</span>
                </div>
              )}
              <div className="h-px bg-gray-200 my-3"></div>
              <div className="flex justify-between items-baseline">
                <span className="text-[12px] font-black text-[#002040] uppercase">Grand Total</span>
                <span className="text-2xl font-black text-[#002040] tracking-tighter">AED {Number(invoice?.grandTotal).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* BANKING & SIGNATURE */}
          <div className="mt-10 grid grid-cols-5 gap-8 items-end">
            <div className="col-span-3">
              <div className="bg-[#002040] text-white text-[9px] font-bold px-4 py-2 rounded-t-lg tracking-widest uppercase">
                Bank Account Information
              </div>
              <div className="p-4 border border-gray-200 border-t-0 rounded-b-lg space-y-1 text-[10px] shadow-sm">
                <p><span className="text-gray-400 font-bold w-24 inline-block uppercase">Account Title:</span> <span className="font-bold text-[#002040]">REKONIC TRAVEL LLC</span></p>
                <p><span className="text-gray-400 font-bold w-24 inline-block uppercase">Account #:</span> <span className="font-bold">010XXXXXXXXXX</span></p>
                <p><span className="text-gray-400 font-bold w-24 inline-block uppercase">Bank Name:</span> <span className="font-bold uppercase">MASHREQ BANK, SHARJAH</span></p>
                <p><span className="text-gray-400 font-bold w-24 inline-block uppercase">IBAN:</span> <span className="font-bold tracking-wider">AE07023XXXXXXXXXXXXXXX</span></p>
              </div>
            </div>

            <div className="col-span-2 flex flex-col items-center relative">
              <div className="h-24 w-full relative flex items-center justify-center">
                <img 
                  src={(config.stampUrl && config.stampUrl !== "null" && config.stampUrl !== "undefined") ? config.stampUrl : "/stamp.jpg"} 
                  alt="Company Stamp" 
                  style={{
                    maxHeight: "100px",
                    maxWidth: "100px",
                    opacity: 0.9,
                    zIndex: 10
                  }}
                  crossOrigin="anonymous"
                />
              </div>
              <div className="w-full border-t-2 border-[#002040] text-center pt-2 mt-4">
                <p className="font-black text-[10px] text-[#002040] uppercase tracking-widest">Authorised Signature</p>
              </div>
            </div>
          </div>

          {/* FOOTER NOTE */}
          <div className="mt-8 pt-6 border-t border-gray-100 text-[9px] text-gray-400 text-center leading-relaxed italic">
            <p>"This is a computer generated document from Rekonic Travel LLC and does not require a physical signature."</p>
            <p>Thank you for choosing Rekonic for your travel needs.</p>
          </div>

        </div>
      </div>
    </div>
  );
});

InvoiceGenerator.displayName = "InvoiceGenerator";
export default InvoiceGenerator;
